"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Quote, CircleCheck, ArrowRight, ArrowLeft } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const testimonials = [{
  author: "Leah K.",
  quote: "Freetown Family Clinic treated me with such dignity. The doctors truly listen, making me feel like a partner in my own health journey.",
  position: "",
  badge: "Verified Patient",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1612706/img/avatar_adama_k.png"
}, {
  author: "David O.",
  quote: "Finding a clinic that understands our culture and provides top-tier medical care is a blessing. My children actually look forward to their check-ups.",
  position: "Local Parent",
  badge: "Verified Family",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1612706/img/avatar_david_o.png"
}, {
  author: "Elena M.",
  quote: "The chronic disease management plan they created for me has changed my life. I feel more energetic and supported than ever before.",
  position: "Patient since 2018",
  badge: "Verified Patient",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1612706/img/avatar_elena_m.png"
}];
export default function VoicesOfHowardCounty() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="27260" id="voices-of-our-patients" className="dark bg-background py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-6 md:px-12">
        <div ref={ref} className={cn("transition-all duration-1000 ease-out transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          {}
          <div className="mb-16 md:mb-24 max-w-2xl">
            <h2 className="text-primary font-serif text-4xl md:text-5xl lg:text-7xl leading-tight mb-6 tracking-tight">
              Voices of Our Patients
            </h2>
            <p className="text-muted-foreground text-lg md:text-xl font-sans max-w-lg leading-relaxed">Honest experiences from our patient community, reflecting the personalized care and cultural trust we build every day.</p>
          </div>

          {}
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4 md:-ml-8">
              {testimonials.map((item, i) => <CarouselItem key={i} data-index={i} className="pl-4 md:pl-8 md:basis-4/5 lg:basis-3/4 xl:basis-2/3">
                  <div className="group relative flex flex-col h-full p-2">
                    {}
                    <Quote className="absolute -top-4 -left-2 size-16 md:size-24 text-primary/10 rotate-180 pointer-events-none select-none" />

                    {}
                    <blockquote className="relative z-10 mb-10 md:mb-14">
                      <p className="font-serif italic text-3xl md:text-4xl lg:text-6xl leading-tight text-foreground tracking-tighter">
                        &ldquo;{item.quote}&rdquo;
                      </p>
                    </blockquote>

                    {}
                    <div className="mt-auto flex items-center gap-4 md:gap-6">
                      <div className="relative">
                        <div className="absolute inset-0 rounded-full bg-primary/20 scale-110 blur-md" />
                        <SafeHydrate><Avatar className="size-14 md:size-16 border-2 border-border shadow-xl relative z-10">
                          <AvatarImage src={item.image} alt={item.author} className="object-cover" />
                          <AvatarFallback className="bg-secondary text-secondary-foreground text-lg font-serif">
                            {item.author.charAt(0)}
                          </AvatarFallback>
                        </Avatar></SafeHydrate>
                      </div>

                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-sans font-semibold text-lg text-foreground tracking-wide">
                            {item.author}
                          </span>
                          <Badge variant="secondary" className="rounded-full px-3 py-0.5 text-xs font-medium bg-secondary/10 text-secondary border-secondary/20">
                            <CircleCheck className="size-3 mr-1 fill-secondary/20" />
                            {item.badge}
                          </Badge>
                        </div>
                        <span className="text-muted-foreground text-sm font-sans">
                          {item.position}
                        </span>
                      </div>
                    </div>
                  </div>
                </CarouselItem>)}
            </CarouselContent>

            {}
            <div className="flex justify-end gap-3 mt-12 md:mt-16 pr-4">
              <CarouselPrevious variant="outline" className="static translate-y-0 size-12 rounded-full border-border hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300 shadow-md">
                <ArrowLeft className="size-5" />
              </CarouselPrevious>
              <CarouselNext variant="outline" className="static translate-y-0 size-12 rounded-full border-border hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300 shadow-md">
                <ArrowRight className="size-5" />
              </CarouselNext>
            </div>
          </Carousel>
        </div>
      </div>
    </section>;
}